import type { ICheckoutContext } from '@/types/context';
import { getDeviceType } from './device';
import type { ProviderEnum } from '@/types/common';
import { getEnvVariable } from '@/utils/get-env-variable';

interface MixpanelProperties {
  browser?: string;
  browser_version?: string;
  currency?: string;
  device_type?: string;
  environment?: string;
  ip_country?: string;
  ip_state?: string;
  offer_type?: string;
  OS?: string;
  OS_version?: string;
  payment_methods?: string[];
  player_id?: string;
  price?: number;
  products?: Array<{ name: string; sku: string; quantity: string }>;
  publisher_id?: string;
  purchase_id?: string;
  payment_method?: string;
  payment_provider?: string;
  promo_code_id?: string;
  checkout_type?: string;
  template_type?: string;
  time?: string;
  load_time?: number;
  session_id?: string;
}

/**
 * Parses browser name and version from user agent
 */
function parseBrowser(userAgent: string): { browser: string; browser_version: string } {
  const ua = userAgent.toLowerCase();
  let browser = 'unknown';
  let browser_version = 'unknown';

  // Chrome
  if (ua.includes('chrome') && !ua.includes('edg') && !ua.includes('opr')) {
    browser = 'Chrome';
    const match = ua.match(/chrome\/([\d.]+)/);
    browser_version = match ? match[1] : 'unknown';
  }
  // Edge
  else if (ua.includes('edg')) {
    browser = 'Edge';
    const match = ua.match(/edg\/([\d.]+)/);
    browser_version = match ? match[1] : 'unknown';
  }
  // Firefox
  else if (ua.includes('firefox')) {
    browser = 'Firefox';
    const match = ua.match(/firefox\/([\d.]+)/);
    browser_version = match ? match[1] : 'unknown';
  }
  // Safari
  else if (ua.includes('safari') && !ua.includes('chrome')) {
    browser = 'Safari';
    const match = ua.match(/version\/([\d.]+)/);
    browser_version = match ? match[1] : 'unknown';
  }
  // Opera
  else if (ua.includes('opr')) {
    browser = 'Opera';
    const match = ua.match(/opr\/([\d.]+)/);
    browser_version = match ? match[1] : 'unknown';
  }

  return { browser, browser_version };
}

/**
 * Parses OS name and version from user agent
 */
function parseOS(userAgent: string): { OS: string; OS_version: string } {
  const ua = userAgent.toLowerCase();
  let OS = 'unknown';
  let OS_version = 'unknown';

  // Windows
  if (ua.includes('windows')) {
    OS = 'Windows';
    if (ua.includes('windows nt 10.0')) OS_version = '10';
    else if (ua.includes('windows nt 6.3')) OS_version = '8.1';
    else if (ua.includes('windows nt 6.2')) OS_version = '8';
    else if (ua.includes('windows nt 6.1')) OS_version = '7';
    else {
      const match = ua.match(/windows nt ([\d.]+)/);
      OS_version = match ? match[1] : 'unknown';
    }
  }
  // macOS
  else if (ua.includes('mac os')) {
    OS = 'macOS';
    const match = ua.match(/mac os x ([\d_]+)/);
    if (match) {
      OS_version = match[1].replace(/_/g, '.');
    } else {
      OS_version = 'unknown';
    }
  }
  // Android
  else if (ua.includes('android')) {
    OS = 'Android';
    const match = ua.match(/android ([\d.]+)/);
    OS_version = match ? match[1] : 'unknown';
  }
  // iOS
  else if (ua.includes('iphone') || ua.includes('ipad')) {
    OS = 'iOS';
    const match = ua.match(/os ([\d_]+)/);
    if (match) {
      OS_version = match[1].replace(/_/g, '.');
    } else {
      OS_version = 'unknown';
    }
  }
  // Linux
  else if (ua.includes('linux')) {
    OS = 'Linux';
    OS_version = 'unknown';
  }

  return { OS, OS_version };
}

/**
 * Extracts mixpanel properties from checkout context
 */
export function getMixpanelProperties(
  context?: ICheckoutContext,
  additionalProperties?: Partial<MixpanelProperties>
): MixpanelProperties {
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const { browser, browser_version } = parseBrowser(userAgent);
  const { OS, OS_version } = parseOS(userAgent);
  const device_type = getDeviceType();
  const session_id = context?.sessionId;
  // Get payment method and provider
  const selectedPaymentMethod = context?.paymentMethodsSettings?.find(
    (pm) => pm.paymentMethodName === context?.paymentMethodSelected
  );

  return {
    browser,
    browser_version,
    session_id,
    currency: context?.pricing?.currencyCode,
    device_type,
    environment: getEnvVariable('VITE_ENV', 'production'),
    ip_country: context?.customer?.geoLocator?.countryCode2,
    ip_state: context?.customer?.geoLocator?.state,
    offer_type: context?.purchasedItems?.[0]?.sku ? 'bundle' : undefined, // You may need to adjust this based on your data structure
    OS,
    OS_version,
    payment_methods: context?.paymentMethodsSettings?.map((pm) => pm.paymentMethodName),
    player_id: context?.customer?.id,
    price: context?.pricing?.priceBreakdown?.priceTotal,
    products: context?.purchasedItems?.map((item) => ({
      name: item.name,
      sku: item.sku,
      quantity: item.quantity,
    })),
    publisher_id: context?.publisher?.id || context?.publisherId,
    purchase_id: context?.redirectPayment?.orderId || context?.successScreen?.orderData?.publisherPurchaseId,
    payment_method: selectedPaymentMethod?.paymentMethodName,
    payment_provider: selectedPaymentMethod?.provider as ProviderEnum,
    checkout_type: 'V3',
    template_type: context?.template?.type,
    time: Date.now().toString(),
    ...additionalProperties,
  };
}
